/** Day view: the hours across the top, and a shift row's batches placed on the hour track. */
import { addDays, format } from "date-fns";
import React from "react";

import { CrewItem, CrewRow } from "../../utils/crew-board"; // eslint-disable-line sort-imports
import { packRows } from "../../utils/timeline"; // eslint-disable-line sort-imports
import { eyebrowSx, lbb } from "../../utils/tokens";

/** Day view: the week as an hour track, scrolled to the chosen day. */
export const HOUR_PX = 64;
export const TRACK_PX = 7 * 24 * HOUR_PX;
const LANE_PX = 92;
const MIN_CARD_PX = 96;
const hourTick = (h: number) => {
	const hour = ((h % 24) + 24) % 24;
	return `${hour % 12 || 12}${hour < 12 ? "a" : "p"}`;
};

// ---------------------------------------------------------------- Day view: the hour track

/** Day names over hour ticks, the whole week, so the track scrolls on into the next days. */
export const HourHeader: React.FC<{ weekStart: Date }> = ({ weekStart }) => (
	<div style={{ position: "sticky", top: 0, zIndex: 3, height: 56, background: lbb.paper, borderBottom: lbb.borderInk, borderRight: lbb.borderHair }}>
		{Array.from({ length: 7 }, (_, d) => (
			<div key={`d${d}`} style={{ position: "absolute", left: d * 24 * HOUR_PX, top: 0, width: 24 * HOUR_PX, height: 30, borderLeft: d ? lbb.borderInk : undefined }} />
		))}
		{/* The day's name every six hours, so it is in view wherever the track is scrolled. */}
		{Array.from({ length: 7 * 4 }, (_, q) => (
			<span key={`q${q}`} style={{ position: "absolute", left: q * 6 * HOUR_PX + 8, top: 7, whiteSpace: "nowrap" }}>
				<span style={{ ...eyebrowSx, fontSize: 10, color: lbb.ink60, marginRight: 6 }}>{format(addDays(weekStart, Math.floor(q / 4)), "EEE")}</span>
				<span style={{ fontFamily: lbb.fontBrand, fontSize: 14, fontWeight: 700, color: lbb.navy }}>{format(addDays(weekStart, Math.floor(q / 4)), "d MMM")}</span>
			</span>
		))}
		{Array.from({ length: 7 * 24 }, (_, h) => (
			<span key={`h${h}`} style={{ position: "absolute", left: h * HOUR_PX, top: 34, width: HOUR_PX, paddingLeft: 4, boxSizing: "border-box", borderLeft: h % 24 === 0 ? lbb.borderInk : lbb.borderHair, fontFamily: lbb.fontMono, fontSize: 10.5, color: lbb.ink60 }}>
				{hourTick(h)}
			</span>
		))}
	</div>
);

/** One shift row's batches at their hours: off-shift time shaded, overlapping batches stacked. */
export const RowTrack: React.FC<{
	row: CrewRow;
	border: React.CSSProperties;
	render: (item: CrewItem, style: React.CSSProperties) => React.ReactNode;
}> = ({ row, border, render }) => {
	const items = row.days.flat();
	// Pack by drawn width (a short part still gets a readable card).
	const { rows, placed } = packRows(items.map((item) => ({ item, start: item.start, end: Math.max(item.end, item.start + MIN_CARD_PX / HOUR_PX) })));
	return (
		<div style={{ ...border, position: "relative", height: Math.max(1, rows) * LANE_PX + 8, background: lbb.alloy }}>
			{row.windows.map((w, d) =>
				w ? <div key={`w${d}`} style={{ position: "absolute", top: 0, bottom: 0, left: w.start * HOUR_PX, width: (w.end - w.start) * HOUR_PX, background: lbb.paper }} /> : null
			)}
			{Array.from({ length: 7 }, (_, d) => (
				<div key={`l${d}`} style={{ position: "absolute", top: 0, bottom: 0, left: d * 24 * HOUR_PX, borderLeft: d ? lbb.borderInk : undefined }} />
			))}
			{placed.map(({ item: { item }, row: lane }) =>
				render(item, {
					position: "absolute",
					top: 4 + lane * LANE_PX,
					left: item.start * HOUR_PX + 2,
					width: Math.max(MIN_CARD_PX, (item.end - item.start) * HOUR_PX) - 4,
					height: LANE_PX - 6,
					overflow: "hidden",
				})
			)}
		</div>
	);
};
